import { useState } from "react";
import axios from "axios";
import { Capacitor } from "@capacitor/core";
import { SignInWithApple } from "@capacitor-community/apple-sign-in";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { useAuth, persistSessionToken } from "../App";
import { readStoredReferrer } from "../lib/referrals";
import logger from "../lib/logger";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// `aud` Apple stamps on the identityToken. For native iOS sign-in this
// is the app bundle id (must match Capacitor's appId AND the backend's
// APPLE_CLIENT_ID env var).
const APPLE_CLIENT_ID = "com.bilateralcalm.app";

/**
 * Apple sign-in button. Renders ONLY on native iOS (Capacitor.getPlatform()
 * === "ios") — required by Apple App Store Guideline 4.8 to provide an
 * equivalent privacy-focused alternative to Google sign-in, and hidden on
 * web/Android where the native plugin can't run.
 */
export const AppleSignInButton = ({ redirectTo = "/dashboard", compact = false }) => {
  const navigate = useNavigate();
  const { checkAuth } = useAuth();
  const [loading, setLoading] = useState(false);

  if (Capacitor.getPlatform() !== "ios") return null;

  const handleAppleSignIn = async () => {
    setLoading(true);
    try {
      // Browser-friendly nonce/state. `crypto.randomUUID` exists on
      // every iOS 15+ WebView (our minimum). Fallback path uses
      // `crypto.getRandomValues` for a proper CSPRNG-backed 128-bit
      // hex string — never Math.random() in the auth flow (S2245).
      const generateSecureRandom = () => {
        if (typeof crypto !== "undefined" && crypto.randomUUID) {
          return crypto.randomUUID();
        }
        const arr = new Uint8Array(16);
        crypto.getRandomValues(arr);
        return Array.from(arr, (b) => b.toString(16).padStart(2, "0")).join("");
      };
      const nonce = generateSecureRandom();
      const state = generateSecureRandom();

      const result = await SignInWithApple.authorize({
        clientId: APPLE_CLIENT_ID,
        // Native iOS doesn't actually redirect anywhere — the plugin
        // still requires the option, so we point it at our backend's
        // app-store-connect-registered callback URL for symmetry.
        redirectURI: `${BACKEND_URL}/api/auth/apple/callback`,
        scopes: "email name",
        state,
        nonce,
      });

      const identityToken = result?.response?.identityToken;
      const givenName = result?.response?.givenName;
      const familyName = result?.response?.familyName;
      const email = result?.response?.email;

      if (!identityToken) {
        throw new Error("Apple did not return an identityToken");
      }

      const { data } = await axios.post(
        `${API}/auth/apple`,
        {
          identity_token: identityToken,
          nonce,
          email,
          given_name: givenName,
          family_name: familyName,
          ...(readStoredReferrer() ? { ref: readStoredReferrer() } : {}),
        }
      );
      // Persist the session_token from the response body so the Bearer
      // interceptor picks it up before checkAuth fires.
      persistSessionToken(data);

      // Refresh the AuthContext so the rest of the app picks up the new
      // user without a full reload, then route.
      await checkAuth();
      navigate(redirectTo, { replace: true });
    } catch (err) {
      // Every ASAuthorizationError code is a user-context issue, not a
      // code bug we can fix:
      //   1000 unknown           — usually iCloud sync / 2FA not set up
      //   1001 canceled          — user tapped Cancel
      //   1002 invalidResponse   — malformed response from Apple, transient
      //   1003 notHandled        — Apple's system couldn't process
      //   1004 failed            — generic device / config failure
      //   1005 notInteractive    — background flow blocked
      // We swallow the entire domain silently (no admin email) and just
      // show a helpful toast so the user can pick another sign-in method.
      // If a truly novel error slips through (non-Apple-auth exception),
      // we still logger.error it with the full serialized error object so
      // future email alerts contain err.code + err.domain, not just a
      // truncated localized message.
      const rawCode = err?.code;
      const rawMsg = String(err?.message || "");
      const rawDomain = String(err?.domain || "");
      const isAppleAuthError =
        // Numeric or string codes 1000–1005 from the Apple plugin
        [1000, 1001, 1002, 1003, 1004, 1005].some((n) => rawCode === n || rawCode === String(n)) ||
        // Symbolic code names some plugin versions emit
        /ASAuthorizationError/i.test(String(rawCode || "")) ||
        // Error message from Apple's system framework
        /AuthenticationServices\.AuthorizationError/i.test(rawMsg) ||
        rawDomain === "com.apple.AuthenticationServices.AuthorizationError" ||
        // Manual /cancel/ fallback so we still swallow legacy cases
        /cancel/i.test(rawMsg);

      const wasCanceled =
        rawCode === 1001 ||
        rawCode === "1001" ||
        rawCode === "ASAuthorizationErrorCanceled" ||
        /error\s*1001\b/i.test(rawMsg) ||
        /cancel/i.test(rawMsg);

      if (isAppleAuthError) {
        if (!wasCanceled) {
          // Not a cancel — user's Apple flow failed. Nudge them toward a
          // working alternative without spamming admin.
          toast.error(
            "Apple sign-in couldn't complete. Please try again, or use email + password."
          );
        }
        return;
      }

      // Truly unexpected — serialize enough context so the email alert has
      // the code + domain, not a 120-char truncated localized string.
      const serialized = (() => {
        try {
          return JSON.stringify({
            code: rawCode ?? null,
            domain: rawDomain || null,
            message: rawMsg,
          });
        } catch { return rawMsg; }
      })();
      logger.error(`Apple sign-in failed [details=${serialized}]`, err);
      toast.error("Apple sign-in failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Apple's HIG-compliant black pill button. Matches the Google button
  // height so the two stack cleanly.
  return (
    <button
      type="button"
      onClick={handleAppleSignIn}
      disabled={loading}
      className={`w-full justify-center rounded-full font-medium ${
        compact ? "py-2.5 text-sm" : "py-3"
      } bg-black text-white hover:bg-neutral-800 active:scale-[0.98] transition-all duration-200 inline-flex items-center gap-2 disabled:opacity-60`}
      data-testid="apple-signin-btn"
      aria-label="Sign in with Apple"
    >
      <svg
        viewBox="0 0 24 24"
        width="16"
        height="16"
        fill="currentColor"
        aria-hidden="true"
      >
        <path d="M16.365 1.43c0 1.14-.42 2.22-1.18 3.03-.79.86-2.04 1.5-3.05 1.4-.13-1.08.42-2.23 1.15-2.97.81-.83 2.18-1.45 3.08-1.46zM20.5 17.36c-.61 1.34-.9 1.93-1.68 3.1-1.1 1.65-2.66 3.7-4.59 3.72-1.7.02-2.14-1.1-4.46-1.09-2.32.01-2.8 1.11-4.5 1.09-1.94-.02-3.41-1.88-4.51-3.53C-2.16 16.27-2.46 9.92.83 6.66c1.16-1.16 2.78-1.83 4.32-1.83 1.9 0 3.09 1.04 4.66 1.04 1.52 0 2.45-1.04 4.65-1.04 1.38 0 2.85.75 3.9 2.05-3.43 1.88-2.87 6.78.14 8.48z" />
      </svg>
      {loading ? "Signing in..." : "Sign in with Apple"}
    </button>
  );
};

export default AppleSignInButton;
